import { useOpenCopilot } from 'copilot-toolkit/hooks/useOpenCopilot';
import { SettingsIcon } from 'foundations-assets/icons/components/SettingsIcon';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { gate } from 'hub-http/gates';
import { getFullUrl } from 'hubspot-url-utils';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import PortalIdParser from 'PortalIdParser';
import { useMemo } from 'react';
import { useConnectionSettingsModal } from 'connections-embed-lib';
import { useIsStandardizedInstallQuery } from '../../../__generated__/chirp/com/hubspot/marketplace/listing/details/rpc/MarketplaceListingDetailsRpc-app.hooks';
import { useGetStandardizedInstallOptInQuery } from '../../../__generated__/chirp/com/hubspot/marketplace/listing/details/rpc/StandardizedInstallOptInRpc.hooks';
import { useGetStandardizedInstallTypeQuery } from '../../../__generated__/chirp/com/hubspot/marketplace/listing/details/rpc/StandardizedInstallTypeRpc.hooks';
import { useGetSatisfyingOptionsQuery } from '../../../__generated__/chirp/com/hubspot/scopegaps/rpc/ScopeGapsService.hooks';
import { useDetailsPageContext } from 'marketplace-ui-common/context/DetailsPageContext';
import { getFormattedShortlinkUrl, getInstalledAppOverviewUrl } from 'marketplace-ui-common/utils/DetailsPageUrls';
import { getAppInstallType } from '../../utils/getAppInstallType';
import { trackCustomMarketplaceOfferingBehavioralEvent } from 'marketplace-ui-common/utils/TrackingUtils';
import { AppUninstallableMessage } from '../AppUninstallableMessage';
import { useAppStatus } from './useAppStatus';
import { useAppUninstallableReason } from './useAppUninstallableReason';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const UPDATE_AVAILABLE_CAUSES = new Set(['release_mgmt_perm_change', 'release_mgmt_major_update', 'release_mgmt_update_required']);

//TODO consider stricter typing for each installState, ex: if UNINSTALLABLE force uninstallableReason to be defined

export function useGetInstallConfiguration({
  productType,
  appContext,
  name,
  installUrl,
  sourceId,
  connectionType,
  listingId,
  features: __features,
  published,
  isMcpServer,
  isDashworks,
  isOpsHub,
  isMcpConnector,
  hasMcpServerAccess,
  isUnifiedAccount,
  isUngatedForConnectionEmbed = false,
  onEmbedClose
}) {
  var _userInfo$gates, _installTypeData$inst, _userInfo$user;
  const {
    trackInteraction,
    isEmbedded,
    previewListingId,
    userInfo
  } = useDetailsPageContext();
  const isUngatedForInstallTypeService = userInfo === null || userInfo === void 0 || (_userInfo$gates = userInfo.gates) === null || _userInfo$gates === void 0 ? void 0 : _userInfo$gates.includes(gate('Marketplace:InstallType'));
  const openCopilot = useOpenCopilot();
  const {
    missingScopeGroups,
    missingScopes,
    uninstallableReason,
    loading: installabilityLoading,
    refetch: refetchInstallability
  } = useAppUninstallableReason({
    appContext,
    features: __features,
    hasMcpServerAccess,
    isMcpServer,
    listingId,
    published
  });
  const {
    data: installTypeData,
    loading: installTypeLoading
  } = useGetStandardizedInstallTypeQuery({
    variables: {
      listingId: previewListingId || listingId,
      published
    },
    skip: !listingId && !previewListingId || !isUngatedForInstallTypeService
  });

  // All remaining RPC calls until installType is defined support the LegacyInstallType checks
  // Can remove all when Marketplace:InstallType gate is cleaned up
  const {
    data: optInData,
    loading: optInLoading
  } = useGetStandardizedInstallOptInQuery({
    variables: {
      listingId: previewListingId || 0
    },
    skip: !previewListingId || appContext === 'admin-preview' || isUngatedForInstallTypeService
  });
  const {
    data: standardizedInstallData,
    loading: standardizedInstallLoading
  } = useIsStandardizedInstallQuery({
    variables: {
      listingId: listingId || 0
    },
    skip: !listingId || appContext !== 'app' || connectionType !== 'STANDARD' || isUngatedForInstallTypeService
  });
  const isStandardizedInstall = standardizedInstallData === null || standardizedInstallData === void 0 ? void 0 : standardizedInstallData.standardizedInstall;
  const isPreviewAndOptedInToStandardInstall = (optInData === null || optInData === void 0 ? void 0 : optInData.optedIn) || false;
  const installType = isUngatedForInstallTypeService ? (_installTypeData$inst = installTypeData === null || installTypeData === void 0 ? void 0 : installTypeData.installType) !== null && _installTypeData$inst !== void 0 ? _installTypeData$inst : 'NONE' : getAppInstallType({
    connectionType,
    isDashworks,
    isMcpServer,
    isOpsHub,
    isStandardizedInstall,
    isPreviewAndOptedInToStandardInstall,
    isUnifiedAccount
  });
  const reconnectLabel = /*#__PURE__*/_jsx(FormattedMessage, {
    message: "detailsPage.productDetailsCard.cta.reconnect"
  });

  // Fetch app status for installed apps (also fetch for missing permissions to show correct reconnect label)
  const {
    statusData,
    statusLoading,
    statusError,
    pollForStatus,
    refetch: refetchStatusData
  } = useAppStatus({
    appId: sourceId,
    shouldFetch: appContext === 'app' && ((uninstallableReason === null || uninstallableReason === void 0 ? void 0 : uninstallableReason.label) === 'ALREADY_INSTALLED' || (uninstallableReason === null || uninstallableReason === void 0 ? void 0 : uninstallableReason.label) === 'MISSING_USER_PERMISSIONS'),
    currentUserId: (userInfo === null || userInfo === void 0 || (_userInfo$user = userInfo.user) === null || _userInfo$user === void 0 ? void 0 : _userInfo$user.user_id) || 0
    // !isPreview, //TODO VERIFY IF NECESSARY
  });
  const {
    open: openConnectionSettingsModal
  } = useConnectionSettingsModal({
    onClose: () => {
      /*
       * Refetch app status and installability to ensure the latest data is used
       * in case app was reconnected/uninstalled from the connection settings modal
       */
      void refetchStatusData();
      void refetchInstallability();
    }
  });
  const appStatusResult = statusData === null || statusData === void 0 ? void 0 : statusData.find(result => result.appId.toString() === (sourceId === null || sourceId === void 0 ? void 0 : sourceId.toString()));
  const {
    type: statusType,
    cause: statusCause,
    cta: {
      link
    } = {},
    externalAccountId,
    unifiedAccountId
  } = appStatusResult || {};

  // Interpolate portalId into the reconnect link and ensure it uses app.hubspot.com
  const reconnectUrl = useMemo(() => {
    if (!link) return undefined;
    const portalId = PortalIdParser.get();
    const interpolatedLink = link.replace('${portalId}', String(portalId));
    return `${getFullUrl('app')}${interpolatedLink}`;
  }, [link]);
  const installState = useMemo(() => {
    // TODO: potential to support more than just 'disconnected' status types for RECOMMENDED_ACTION
    if (!statusLoading && !statusError && statusType === 'disconnected') {
      return 'RECOMMENDED_ACTION';
    } else if (!statusLoading && !statusError && statusCause && UPDATE_AVAILABLE_CAUSES.has(statusCause)) {
      return 'UPDATE_AVAILABLE'; //release management "update" scenario
    } else if ((uninstallableReason === null || uninstallableReason === void 0 ? void 0 : uninstallableReason.label) === 'ALREADY_INSTALLED' || installType === 'HOMEMADE_PREINSTALLED') {
      return 'INSTALLED';
    } else if (uninstallableReason) {
      return 'UNINSTALLABLE';
    } else {
      return 'INSTALLABLE';
    }
  }, [uninstallableReason, installType, statusLoading, statusError, statusType, statusCause]);
  const requestInstallLabel = /*#__PURE__*/_jsx(FormattedMessage, {
    message: installState === 'RECOMMENDED_ACTION' ? 'detailsPage.productDetailsCard.cta.requestReconnect' : 'detailsPage.productDetailsCard.cta.request'
  });
  const {
    data: missingPlanData
  } = useGetSatisfyingOptionsQuery({
    variables: {
      scopes: missingScopes,
      scopeGroups: missingScopeGroups
    },
    skip: installabilityLoading || missingScopes.length === 0 && missingScopeGroups.length === 0
  });
  const uninstallableTooltipContent = useMemo(() => {
    const missingPlanOptions = missingPlanData === null || missingPlanData === void 0 ? void 0 : missingPlanData.satisfyingOptions;
    if ((uninstallableReason === null || uninstallableReason === void 0 ? void 0 : uninstallableReason.label) === 'MISSING_HUB_SCOPES' && missingPlanOptions && missingPlanOptions.length === 1 && missingPlanOptions[0].and.length === 1 && missingPlanOptions[0].and[0].or[0].displayName // skip if missing a display name
    ) {
      if (missingPlanOptions[0].and[0].or.length === 1) {
        const option = missingPlanOptions[0].and[0].or[0];
        return /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.productDetailsCard.cta.uninstallableReasons.missingPlan",
          options: {
            planName: option.displayName,
            productType: unescapedText(`detailsPage.productType.${productType}`)
          }
        });
      } else {
        return /*#__PURE__*/_jsxs("span", {
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            message: "detailsPage.productDetailsCard.cta.uninstallableReasons.missingPlans",
            options: {
              productType: unescapedText(`detailsPage.productType.${productType}`)
            }
          }), /*#__PURE__*/_jsx(List, {
            marginBlockStart: "200",
            marginInlineStart: "-400",
            styled: true,
            children: missingPlanOptions[0].and[0].or.map((option, index) => /*#__PURE__*/_jsx(ListItem, {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "detailsPage.productDetailsCard.cta.uninstallableReasons.orHigherTier",
                options: {
                  planName: option.displayName
                }
              })
            }, index))
          })]
        });
      }
    }
    return /*#__PURE__*/_jsx(AppUninstallableMessage, {
      uninstallableReason: uninstallableReason
    });
  }, [missingPlanData === null || missingPlanData === void 0 ? void 0 : missingPlanData.satisfyingOptions, productType, uninstallableReason]);
  const parsedProductType = isMcpConnector || isMcpServer ? 'CONNECTOR' : productType;
  const installLabelString = `detailsPage.productDetailsCard.cta.install.${parsedProductType}`;
  const installLabel = /*#__PURE__*/_jsx(FormattedMessage, {
    message: installLabelString
  });
  const installLabelText = I18n.text(installLabelString);
  let postInstallLabel = /*#__PURE__*/_jsx(_Fragment, {});
  let postInstallRoute = undefined;
  let postInstallOnClick = () => {};
  switch (installType) {
    case 'HOMEMADE_DASHWORKS':
      if (isEmbedded) {
        postInstallLabel = /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(SettingsIcon, {}), /*#__PURE__*/_jsx(FormattedMessage, {
            message: `detailsPage.productDetailsCard.cta.postInstall.${parsedProductType}`
          })]
        });
        postInstallRoute = isUngatedForConnectionEmbed && sourceId ? undefined : getInstalledAppOverviewUrl(sourceId !== null && sourceId !== void 0 ? sourceId : '');
        postInstallOnClick = () => {
          trackInteraction({
            action: 'click manage app button'
          });
          trackCustomMarketplaceOfferingBehavioralEvent('MANAGE_APP_CLICK');
          if (sourceId && isUngatedForConnectionEmbed) {
            onEmbedClose === null || onEmbedClose === void 0 || onEmbedClose();
            openConnectionSettingsModal(sourceId.toString(), {
              trackerEventProperties: {
                app: 'marketplace'
              }
            });
          }
        };
      } else {
        postInstallLabel = /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.productDetailsCard.cta.openBreeze"
        });
        postInstallOnClick = () => {
          openCopilot();
          trackInteraction({
            action: 'click open breeze dashworks button'
          });
        };
      }
      break;
    case 'HOMEMADE_PREINSTALLED':
      postInstallLabel = /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.productDetailsCard.cta.preinstalled",
        options: {
          name
        }
      });
      postInstallRoute = getFormattedShortlinkUrl(installUrl);
      postInstallOnClick = () => {
        trackInteraction({
          action: 'click preinstalled learn how to use button'
        });
      };
      break;
    default:
      postInstallLabel = /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(SettingsIcon, {}), /*#__PURE__*/_jsx(FormattedMessage, {
          message: `detailsPage.productDetailsCard.cta.postInstall.${parsedProductType}`
        })]
      });
      postInstallRoute = isUngatedForConnectionEmbed && sourceId ? undefined : getInstalledAppOverviewUrl(sourceId !== null && sourceId !== void 0 ? sourceId : '');
      postInstallOnClick = () => {
        trackInteraction({
          action: 'click manage app button'
        });
        trackCustomMarketplaceOfferingBehavioralEvent('MANAGE_APP_CLICK');
        if (sourceId && isUngatedForConnectionEmbed) {
          onEmbedClose === null || onEmbedClose === void 0 || onEmbedClose();
          openConnectionSettingsModal(sourceId.toString(), {
            trackerEventProperties: {
              app: 'marketplace'
            }
          });
        }
      };
  }
  return {
    loading: installabilityLoading || statusLoading || (isUngatedForInstallTypeService ? installTypeLoading : optInLoading || standardizedInstallLoading),
    installState,
    productType,
    installType,
    refetchInstallability,
    //INSTALLABLE
    installLabel,
    installLabelText,
    // UNINSTALLABLE
    uninstallableReason,
    uninstallableTooltipContent,
    requestInstallLabel,
    // INSTALLED
    postInstallLabel,
    postInstallRoute,
    postInstallOnClick,
    // RECOMMENDED_ACTION
    postInstallRecommendedAction: {
      label: reconnectLabel,
      url: reconnectUrl,
      refetch: pollForStatus,
      accountId: externalAccountId,
      unifiedAccountId
    }
  };
}